import { useMemo } from 'react';

import type { LlmPriceVersionDto } from '../../../../../../backend/src/llm/pricing/dto/llm-pricing.response.dto';
import { Badge } from '@/new/components/shadcn/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/new/components/shadcn/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/new/components/shadcn/table';
import { formatDatetimeForTable } from '@/new/lib/table-utils';

const formatRate = (value: number) => `$${Number(value.toFixed(6))}`;

/**
 * Every price version ever recorded for one model, across all geos, newest
 * first — the audit answer to "who changed this rate, to what, and when".
 */
export function PriceHistoryDialog({
  model,
  history,
  onClose,
}: {
  model: string | null;
  history: LlmPriceVersionDto[];
  onClose: () => void;
}) {
  const versions = useMemo(
    () =>
      history
        .filter((price) => price.model === model)
        .sort((a, b) => new Date(b.effectiveFrom).getTime() - new Date(a.effectiveFrom).getTime()),
    [history, model],
  );

  return (
    <Dialog open={model !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-mono">{model?.replace(/^anthropic\./, '') ?? ''}</DialogTitle>
          <DialogDescription>
            {versions.length === 1 ? '1 price version' : `${versions.length} price versions`}, newest first. USD per
            million tokens.
          </DialogDescription>
        </DialogHeader>

        <div className="overflow-x-auto">
          <Table className="w-auto">
            <TableHeader>
              <TableRow>
                <TableHead className="whitespace-nowrap">Geo</TableHead>
                <TableHead>Input</TableHead>
                <TableHead className="whitespace-nowrap">Cache read</TableHead>
                <TableHead className="whitespace-nowrap">Cache write</TableHead>
                <TableHead>Output</TableHead>
                <TableHead className="whitespace-nowrap">Effective from</TableHead>
                <TableHead className="whitespace-nowrap">Added by</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {versions.map((version) => (
                <TableRow key={version.id}>
                  <TableCell className="font-mono whitespace-nowrap">{version.servingGeo}</TableCell>
                  <TableCell className="tabular-nums">{formatRate(version.inputUsdPerMTok)}</TableCell>
                  <TableCell className="tabular-nums">{formatRate(version.cacheReadUsdPerMTok)}</TableCell>
                  <TableCell className="tabular-nums">{formatRate(version.cacheWriteUsdPerMTok)}</TableCell>
                  <TableCell className="tabular-nums">{formatRate(version.outputUsdPerMTok)}</TableCell>
                  <TableCell className="whitespace-nowrap">
                    <span className="flex items-center gap-2">
                      {formatDatetimeForTable(version.effectiveFrom)}
                      {version.isScheduled && <Badge variant="secondary">Scheduled</Badge>}
                    </span>
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-gray-500">
                    {version.createdByEmail ?? 'Seeded'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  );
}
